
import React, { useEffect, useMemo, useState } from "react";

import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Paper,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";

import {
    Add,
    Edit,
    Refresh,
    Search,
    AccountTree,
    Save,
    Clear,
} from "@mui/icons-material";

import {
    getAllocationHeads,
    getDepartments,
    getReportTypes,
    insertAllocationHead,
    updateAllocationHead,
} from "../../../services/allocationHeadService";


const emptyForm = {
    AllocationHead: "",
    DepartmentCode: "",
    ReportCode: "",
    AllocationHeadCode: "",
};


export default function AllocationHead() {

    const [form, setForm] = useState(emptyForm);

    const [rows, setRows] = useState([]);

    const [departments, setDepartments] = useState([]);

    const [reportTypes, setReportTypes] = useState([]);

    const [search, setSearch] = useState("");

    const [editMode, setEditMode] = useState(false);

    const [loading, setLoading] = useState(false);

    const [loadingMaster, setLoadingMaster] = useState(true);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });


    // ==========================================================
    // MESSAGE
    // ==========================================================

    const showMessage = (
        message,
        severity = "success"
    ) => {

        setSnackbar({
            open: true,
            message,
            severity,
        });
    };


    // ==========================================================
    // LOAD EVERYTHING
    // ==========================================================

    const loadData = async () => {

        try {

            setLoadingMaster(true);

            const [
                allocationRes,
                departmentRes,
                reportRes,
            ] = await Promise.all([
                getAllocationHeads(),
                getDepartments(),
                getReportTypes(),
            ]);

            setRows(
                allocationRes.data || []
            );

            setDepartments(
                departmentRes.data || []
            );

            setReportTypes(
                reportRes.data || []
            );

        } catch (error) {

            console.error(
                "Load allocation head data error:",
                error
            );

            showMessage(
                error?.response?.data?.message ||
                "Unable to load data",
                "error"
            );

        } finally {

            setLoadingMaster(false);

        }
    };


    useEffect(() => {
        loadData();
    }, []);


    // ==========================================================
    // FORM CHANGE
    // ==========================================================

    const handleChange = (event) => {

        const {
            name,
            value,
        } = event.target;

        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };


    // ==========================================================
    // SAVE / UPDATE
    // ==========================================================

    const handleSubmit = async () => {

        if (!form.ReportCode) {

            showMessage(
                "Please select Report Type",
                "warning"
            );

            return;
        }


        if (!form.DepartmentCode) {

            showMessage(
                "Please select Department",
                "warning"
            );

            return;
        }


        if (!form.AllocationHeadCode.trim()) {

            showMessage(
                "Please enter Allocation Head Code",
                "warning"
            );

            return;
        }


        if (!form.AllocationHead.trim()) {

            showMessage(
                "Please enter Allocation Head",
                "warning"
            );

            return;
        }


        try {

            setLoading(true);

            if (editMode) {

                await updateAllocationHead(
                    form.AllocationHeadCode,
                    {
                        AllocationHead:
                            form.AllocationHead,

                        DepartmentCode:
                            form.DepartmentCode,

                        ReportCode:
                            form.ReportCode,
                    }
                );

                showMessage(
                    "Allocation Head updated successfully",
                    "success"
                );

            } else {

                await insertAllocationHead(
                    form
                );

                showMessage(
                    "Allocation Head saved successfully",
                    "success"
                );
            }


            await loadData();

            clearForm();

        } catch (error) {

            console.error(
                "Allocation head save error:",
                error
            );

            showMessage(
                error?.response?.data?.message ||
                "Operation failed",
                "error"
            );

        } finally {

            setLoading(false);

        }
    };


    // ==========================================================
    // EDIT
    // ==========================================================

    const handleEdit = (row) => {

        setForm({
            AllocationHead:
                row.AllocationHead || "",

            DepartmentCode:
                row.DepartmentCode || "",

            ReportCode:
                row.ReportCode || "",

            AllocationHeadCode:
                row.AllocationHeadCode || "",
        });

        setEditMode(true);

        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };


    // ==========================================================
    // CLEAR FORM
    // ==========================================================

    const clearForm = () => {

        setForm({
            ...emptyForm,
        });

        setEditMode(false);
    };


    // ==========================================================
    // FILTER
    // ==========================================================

    const filteredRows = useMemo(() => {

        const value =
            search
                .trim()
                .toLowerCase();

        if (!value) {
            return rows;
        }

        return rows.filter((row) => {

            return (

                String(
                    row.AllocationHead || ""
                )
                    .toLowerCase()
                    .includes(value)

                ||

                String(
                    row.AllocationHeadCode || ""
                )
                    .toLowerCase()
                    .includes(value)

                ||

                String(
                    row.DepartmentName || ""
                )
                    .toLowerCase()
                    .includes(value)

                ||

                String(
                    row.DepartmentCode || ""
                )
                    .toLowerCase()
                    .includes(value)

                ||

                String(
                    row.ReportCode || ""
                )
                    .toLowerCase()
                    .includes(value)

            );

        });

    }, [rows, search]);


    // ==========================================================
    // LOADING SCREEN
    // ==========================================================

    if (loadingMaster) {

        return (

            <Box
                sx={{
                    minHeight: "70vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#f5f7fa",
                }}
            >

                <Stack
                    spacing={2}
                    alignItems="center"
                >

                    <CircularProgress
                        size={42}
                    />

                    <Typography
                        color="text.secondary"
                        fontWeight={500}
                    >
                        Loading Allocation Head data...
                    </Typography>

                </Stack>

            </Box>
        );
    }


    // ==========================================================
    // UI
    // ==========================================================

    return (

        <Box
            sx={{
                width: "100%",
                minHeight: "100vh",
                backgroundColor: "#f5f7fa",
                p: {
                    xs: 1.5,
                    sm: 2,
                    md: 3,
                },
                boxSizing: "border-box",
            }}
        >


            {/* ==================================================
                PROFESSIONAL PAGE HEADER
            ================================================== */}

            <PaperHeader
                editMode={editMode}
                total={rows.length}
            />


            {/* ==================================================
                ENTRY FORM
            ================================================== */}

            <Card
                elevation={0}
                sx={{
                    mb: 3,
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    overflow: "hidden",
                    backgroundColor: "#ffffff",
                }}
            >

                {/* FORM HEADER */}

                <Box
                    sx={{
                        px: {
                            xs: 2,
                            sm: 3,
                        },
                        py: 2.5,
                        background:
                            "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                        color: "#ffffff",
                    }}
                >

                    <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                    >

                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor:
                                    "rgba(255,255,255,0.18)",
                            }}
                        >
                            {editMode
                                ? <Edit />
                                : <Add />
                            }
                        </Box>

                        <Box>

                            <Typography
                                variant="h6"
                                fontWeight={700}
                            >
                                {editMode
                                    ? "Update Allocation Head"
                                    : "New Allocation Head"
                                }
                            </Typography>

                            <Typography
                                variant="body2"
                                sx={{
                                    mt: 0.4,
                                    opacity: 0.9,
                                }}
                            >
                                {editMode
                                    ? "Modify allocation head details"
                                    : "Create a new allocation head"
                                }
                            </Typography>

                        </Box>

                    </Stack>

                </Box>


                <CardContent
                    sx={{
                        p: {
                            xs: 2,
                            sm: 3,
                        },
                    }}
                >

                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 2,
                        }}
                    >


                        {/* REPORT TYPE */}

                        <Box
                            sx={{
                                flex: {
                                    xs: "1 1 100%",
                                    sm: "1 1 calc(50% - 8px)",
                                    md: "1 1 calc(25% - 12px)",
                                },
                                minWidth: 0,
                            }}
                        >

                            <Autocomplete
                                fullWidth
                                options={reportTypes}
                                value={
                                    reportTypes.find(
                                        (item) =>
                                            item.ReportCode ===
                                            form.ReportCode
                                    ) || null
                                }
                                getOptionLabel={
                                    (option) =>
                                        option.ReportCode ||
                                        ""
                                }
                                onChange={(_, value) => {

                                    setForm((prev) => ({
                                        ...prev,
                                        ReportCode:
                                            value?.ReportCode ||
                                            "",
                                    }));

                                }}
                                disabled={loading}
                                renderInput={(params) => (

                                    <TextField
                                        {...params}
                                        label="Report Type"
                                        size="small"
                                        required
                                        fullWidth
                                    />

                                )}
                            />

                        </Box>


                        {/* DEPARTMENT */}

                        <Box
                            sx={{
                                flex: {
                                    xs: "1 1 100%",
                                    sm: "1 1 calc(50% - 8px)",
                                    md: "1 1 calc(25% - 12px)",
                                },
                                minWidth: 0,
                            }}
                        >

                            <Autocomplete
                                fullWidth
                                options={departments}
                                value={
                                    departments.find(
                                        (item) =>
                                            item.DepartmentCode ===
                                            form.DepartmentCode
                                    ) || null
                                }
                                getOptionLabel={
                                    (option) =>
                                        option.DepartmentName
                                            ? `${option.DepartmentName}`
                                            : ""
                                }
                                onChange={(_, value) => {

                                    setForm((prev) => ({
                                        ...prev,
                                        DepartmentCode:
                                            value?.DepartmentCode ||
                                            "",
                                    }));

                                }}
                                disabled={loading}
                                renderInput={(params) => (

                                    <TextField
                                        {...params}
                                        label="Department"
                                        size="small"
                                        required
                                        fullWidth
                                    />

                                )}
                            />

                        </Box>


                        {/* ALLOCATION HEAD CODE */}

                        <Box
                            sx={{
                                flex: {
                                    xs: "1 1 100%",
                                    sm: "1 1 calc(50% - 8px)",
                                    md: "1 1 calc(25% - 12px)",
                                },
                                minWidth: 0,
                            }}
                        >

                            <TextField
                                fullWidth
                                size="small"
                                label="Allocation Head Code"
                                name="AllocationHeadCode"
                                value={
                                    form.AllocationHeadCode
                                }
                                onChange={
                                    handleChange
                                }
                                required
                                disabled={
                                    editMode ||
                                    loading
                                }
                                helperText={
                                    editMode
                                        ? "Code cannot be changed during update"
                                        : "Enter unique allocation head code"
                                }
                            />

                        </Box>


                        {/* ALLOCATION HEAD */}

                        <Box
                            sx={{
                                flex: {
                                    xs: "1 1 100%",
                                    sm: "1 1 calc(50% - 8px)",
                                    md: "1 1 calc(25% - 12px)",
                                },
                                minWidth: 0,
                            }}
                        >

                            <TextField
                                fullWidth
                                size="small"
                                label="Allocation Head"
                                name="AllocationHead"
                                value={
                                    form.AllocationHead
                                }
                                onChange={
                                    handleChange
                                }
                                required
                                disabled={loading}
                                helperText="Enter allocation head name"
                            />

                        </Box>

                    </Box>


                    {/* FORM ACTIONS */}

                    <Box
                        sx={{
                            mt: 3,
                            pt: 2,
                            borderTop: "1px solid",
                            borderColor: "divider",
                        }}
                    >

                        <Stack
                            direction={{
                                xs: "column-reverse",
                                sm: "row",
                            }}
                            spacing={1.5}
                            justifyContent="flex-end"
                        >

                            <Button
                                variant="outlined"
                                startIcon={
                                    <Clear />
                                }
                                onClick={
                                    clearForm
                                }
                                disabled={
                                    loading
                                }
                                sx={{
                                    minWidth: 110,
                                    borderRadius: 2,
                                    textTransform:
                                        "none",
                                    fontWeight: 600,
                                }}
                            >
                                Clear
                            </Button>


                            <Button
                                variant="contained"
                                startIcon={
                                    editMode
                                        ? <Edit />
                                        : <Save />
                                }
                                onClick={
                                    handleSubmit
                                }
                                disabled={
                                    loading
                                }
                                sx={{
                                    minWidth: 130,
                                    borderRadius: 2,
                                    textTransform:
                                        "none",
                                    fontWeight: 700,
                                    boxShadow: "none",
                                }}
                            >
                                {loading
                                    ? "Saving..."
                                    : editMode
                                        ? "Update"
                                        : "Save"
                                }
                            </Button>

                        </Stack>

                    </Box>

                </CardContent>

            </Card>


            {/* ==================================================
                LIST / SEARCH
            ================================================== */}

            <Card
                elevation={0}
                sx={{
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    overflow: "hidden",
                    backgroundColor: "#ffffff",
                }}
            >

                {/* LIST HEADER */}

                <Box
                    sx={{
                        px: {
                            xs: 2,
                            md: 3,
                        },
                        py: 2.5,
                        backgroundColor: "grey.50",
                    }}
                >

                    <Stack
                        direction={{
                            xs: "column",
                            md: "row",
                        }}
                        spacing={2}
                        justifyContent="space-between"
                        alignItems={{
                            xs: "stretch",
                            md: "center",
                        }}
                    >

                        <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                        >

                            <Box
                                sx={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor:
                                        "rgba(25,118,210,0.1)",
                                    color: "primary.main",
                                }}
                            >
                                <AccountTree />
                            </Box>

                            <Box>

                                <Typography
                                    variant="h6"
                                    fontWeight={700}
                                >
                                    Allocation Head List
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Manage configured allocation
                                    heads and mappings.
                                </Typography>

                            </Box>

                        </Stack>


                        <Stack
                            direction={{
                                xs: "column",
                                sm: "row",
                            }}
                            spacing={1}
                        >

                            <TextField
                                size="small"
                                value={search}
                                onChange={(e) =>
                                    setSearch(
                                        e.target.value
                                    )
                                }
                                placeholder="Search allocation heads..."
                                sx={{
                                    minWidth: {
                                        xs: "100%",
                                        sm: 280,
                                    },
                                }}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <Search
                                                fontSize="small"
                                                sx={{
                                                    mr: 1,
                                                    color:
                                                        "text.secondary",
                                                }}
                                            />
                                        ),
                                    },
                                }}
                            />

                            <Button
                                variant="outlined"
                                startIcon={
                                    <Refresh />
                                }
                                onClick={() => {
                                    setSearch("");
                                    loadData();
                                }}
                                disabled={
                                    loadingMaster
                                }
                                sx={{
                                    borderRadius: 2,
                                    textTransform:
                                        "none",
                                    fontWeight: 600,
                                    minWidth: 110,
                                }}
                            >
                                Refresh
                            </Button>

                        </Stack>

                    </Stack>


                    {/* COUNTERS */}

                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                            mt: 2,
                        }}
                    >

                        <Chip
                            label={`Total: ${rows.length}`}
                            color="primary"
                            variant="outlined"
                            size="small"
                            sx={{
                                fontWeight: 600,
                            }}
                        />

                        <Chip
                            label={`Found: ${filteredRows.length}`}
                            color={
                                filteredRows.length
                                    ? "success"
                                    : "default"
                            }
                            variant="outlined"
                            size="small"
                            sx={{
                                fontWeight: 600,
                            }}
                        />

                        {search && (
                            <Chip
                                label={`Search: "${search}"`}
                                variant="outlined"
                                size="small"
                                onDelete={() =>
                                    setSearch("")
                                }
                            />
                        )}

                    </Stack>

                </Box>


                <Divider />


                {/* TABLE */}

                <TableContainer
                    sx={{
                        maxHeight: {
                            xs: "60vh",
                            sm: 600,
                        },
                        overflowX: "auto",
                    }}
                >

                    <Table
                        stickyHeader
                        size="small"
                        sx={{
                            minWidth: 850,
                        }}
                    >

                        <TableHead>

                            <TableRow>

                                <TableCell
                                    sx={{
                                        backgroundColor:
                                            "#1976d2",
                                        color:
                                            "#ffffff",
                                        fontWeight: 700,
                                        width: 80,
                                    }}
                                >
                                    S.No.
                                </TableCell>


                                <TableCell
                                    sx={{
                                        backgroundColor:
                                            "#1976d2",
                                        color:
                                            "#ffffff",
                                        fontWeight: 700,
                                    }}
                                >
                                    Allocation Head Code
                                </TableCell>


                                <TableCell
                                    sx={{
                                        backgroundColor:
                                            "#1976d2",
                                        color:
                                            "#ffffff",
                                        fontWeight: 700,
                                    }}
                                >
                                    Allocation Head
                                </TableCell>


                                <TableCell
                                    sx={{
                                        backgroundColor:
                                            "#1976d2",
                                        color:
                                            "#ffffff",
                                        fontWeight: 700,
                                    }}
                                >
                                    Department
                                </TableCell>


                                <TableCell
                                    sx={{
                                        backgroundColor:
                                            "#1976d2",
                                        color:
                                            "#ffffff",
                                        fontWeight: 700,
                                    }}
                                >
                                    Report Type
                                </TableCell>


                                <TableCell
                                    align="center"
                                    sx={{
                                        backgroundColor:
                                            "#1976d2",
                                        color:
                                            "#ffffff",
                                        fontWeight: 700,
                                        width: 110,
                                    }}
                                >
                                    Action
                                </TableCell>

                            </TableRow>

                        </TableHead>


                        <TableBody>

                            {filteredRows.length === 0 ? (

                                <TableRow>

                                    <TableCell
                                        colSpan={6}
                                        align="center"
                                    >

                                        <Box
                                            sx={{
                                                py: 7,
                                            }}
                                        >

                                            <AccountTree
                                                sx={{
                                                    fontSize: 48,
                                                    color:
                                                        "text.disabled",
                                                    mb: 1,
                                                }}
                                            />

                                            <Typography
                                                fontWeight={600}
                                            >
                                                No records found
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    mt: 0.5,
                                                }}
                                            >
                                                {search
                                                    ? "Try another search term."
                                                    : "No allocation heads are available."
                                                }
                                            </Typography>

                                        </Box>

                                    </TableCell>

                                </TableRow>

                            ) : (

                                filteredRows.map(
                                    (row, index) => (

                                        <TableRow
                                            key={
                                                row.AllocationHeadCode
                                            }
                                            hover
                                            sx={{
                                                "&:nth-of-type(even)":
                                                    {
                                                        backgroundColor:
                                                            "#fafafa",
                                                    },
                                                "&:hover":
                                                    {
                                                        backgroundColor:
                                                            "rgba(25,118,210,0.05)",
                                                    },
                                            }}
                                        >

                                            <TableCell>
                                                {index + 1}
                                            </TableCell>


                                            <TableCell>

                                                <Chip
                                                    label={
                                                        row.AllocationHeadCode
                                                    }
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    sx={{
                                                        fontWeight: 700,
                                                    }}
                                                />

                                            </TableCell>


                                            <TableCell>

                                                <Typography
                                                    variant="body2"
                                                    fontWeight={600}
                                                >
                                                    {
                                                        row.AllocationHead
                                                    }
                                                </Typography>

                                            </TableCell>


                                            <TableCell>

                                                <Stack
                                                    spacing={0.2}
                                                >

                                                    <Typography
                                                        variant="body2"
                                                    >
                                                        {
                                                            row.DepartmentName ||
                                                            "-"
                                                        }
                                                    </Typography>

                                                    {row.DepartmentCode && (
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            Code:{" "}
                                                            {
                                                                row.DepartmentCode
                                                            }
                                                        </Typography>
                                                    )}

                                                </Stack>

                                            </TableCell>


                                            <TableCell>

                                                <Chip
                                                    label={
                                                        row.ReportCode ||
                                                        "-"
                                                    }
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                        fontWeight: 600,
                                                    }}
                                                />

                                            </TableCell>


                                            <TableCell
                                                align="center"
                                            >

                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() =>
                                                        handleEdit(
                                                            row
                                                        )
                                                    }
                                                    disabled={
                                                        loading
                                                    }
                                                    title="Edit Allocation Head"
                                                    sx={{
                                                        border:
                                                            "1px solid",
                                                        borderColor:
                                                            "primary.light",
                                                        mr: 0.5,
                                                    }}
                                                >
                                                    <Edit
                                                        fontSize="small"
                                                    />
                                                </IconButton>

                                            </TableCell>

                                        </TableRow>

                                    )
                                )

                            )}

                        </TableBody>

                    </Table>

                </TableContainer>


                {/* TABLE FOOTER */}

                <Box
                    sx={{
                        px: {
                            xs: 2,
                            md: 3,
                        },
                        py: 1.5,
                        borderTop: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "grey.50",
                    }}
                >

                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                    >

                        <Typography
                            variant="caption"
                            color="text.secondary"
                        >
                            Allocation Head Records
                        </Typography>

                        <Typography
                            variant="body2"
                            fontWeight={700}
                            color="primary"
                        >
                            {filteredRows.length}
                        </Typography>

                    </Stack>

                </Box>

            </Card>


            {/* ==================================================
                SNACKBAR
            ================================================== */}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() =>
                    setSnackbar((prev) => ({
                        ...prev,
                        open: false,
                    }))
                }
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
            >

                <Alert
                    severity={
                        snackbar.severity
                    }
                    variant="filled"
                    onClose={() =>
                        setSnackbar((prev) => ({
                            ...prev,
                            open: false,
                        }))
                    }
                >
                    {snackbar.message}
                </Alert>

            </Snackbar>

        </Box>
    );
}


/* ==========================================================
   PROFESSIONAL HEADER COMPONENT
========================================================== */

function PaperHeader({
    editMode,
    total,
}) {

    return (

        <Paper
            elevation={0}
            sx={{
                mb: 3,
                p: {
                    xs: 2.5,
                    md: 3.5,
                },
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                background:
                    "linear-gradient(135deg, #f8fbff 0%, #ffffff 60%, #f4f8ff 100%)",
                position: "relative",
                overflow: "hidden",
            }}
        >

            {/* Decorative Circle */}

            <Box
                sx={{
                    position: "absolute",
                    right: -55,
                    top: -70,
                    width: 220,
                    height: 220,
                    borderRadius: "50%",
                    background:
                        "linear-gradient(135deg, rgba(25,118,210,0.12), rgba(25,118,210,0.02))",
                }}
            />


            <Stack
                direction={{
                    xs: "column",
                    sm: "row",
                }}
                spacing={2.5}
                alignItems={{
                    xs: "flex-start",
                    sm: "center",
                }}
                sx={{
                    position: "relative",
                    zIndex: 1,
                }}
            >

                {/* Icon */}

                <Box
                    sx={{
                        width: 64,
                        height: 64,
                        borderRadius: 2.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background:
                            "linear-gradient(135deg, #1976d2, #42a5f5)",
                        color: "#ffffff",
                        boxShadow:
                            "0 8px 20px rgba(25,118,210,0.25)",
                        flexShrink: 0,
                    }}
                >
                    <AccountTree
                        sx={{
                            fontSize: 34,
                        }}
                    />
                </Box>


                {/* Title */}

                <Box
                    sx={{
                        flex: 1,
                    }}
                >

                    <Stack
                        direction={{
                            xs: "column",
                            md: "row",
                        }}
                        spacing={1}
                        alignItems={{
                            xs: "flex-start",
                            md: "center",
                        }}
                    >

                        <Typography
                            variant="h4"
                            fontWeight={800}
                            sx={{
                                fontSize: {
                                    xs: "1.6rem",
                                    md: "2rem",
                                },
                            }}
                        >
                            Allocation Head
                        </Typography>

                        {editMode && (
                            <Chip
                                label="Edit Mode"
                                color="warning"
                                size="small"
                                sx={{
                                    fontWeight: 700,
                                }}
                            />
                        )}

                    </Stack>


                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{
                            mt: 0.5,
                            maxWidth: 700,
                        }}
                    >
                        Configure allocation heads,
                        departments and report type
                        mappings from one place.
                    </Typography>

                </Box>


                {/* TOTAL */}

                <Box
                    sx={{
                        minWidth: {
                            xs: "100%",
                            sm: 170,
                        },
                        textAlign: {
                            xs: "left",
                            sm: "right",
                        },
                    }}
                >

                    <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={700}
                        sx={{
                            textTransform:
                                "uppercase",
                            letterSpacing: 0.8,
                        }}
                    >
                        Total Heads
                    </Typography>


                    <Typography
                        variant="h4"
                        color="primary"
                        fontWeight={800}
                        sx={{
                            lineHeight: 1.2,
                            mt: 0.3,
                        }}
                    >
                        {total.toLocaleString()}
                    </Typography>


                    <Typography
                        variant="caption"
                        color="text.secondary"
                    >
                        configured records
                    </Typography>

                </Box>

            </Stack>

        </Paper>
    );
}

